/**
 * RespondHero widget API client.
 *
 * Two surfaces:
 *  - `fetchConfig` for `GET /widget/v1/config` (one-shot, JSON).
 *  - `streamChat` for `POST /widget/v1/chat`, an async-iterator over
 *    parsed SSE events.
 *
 * Auth in both cases is `Authorization: Widget <slug>:<key>`. The
 * server cross-checks the request's `Origin` against the tenant's
 * `allowed_widget_origins`, so this client never has to know the
 * origin explicitly: the browser will send it.
 */

import type { WidgetBootConfig, WidgetTenantConfig } from "./types";

/**
 * Response shape for `POST /v1/voice/sessions`. Mirrors the backend
 * `VoiceSessionResponse` Pydantic schema. The websocket URL is
 * short-lived; the modal opens it once and refreshes only if the
 * websocket itself drops.
 */
export interface VoiceSession {
  signed_url: string;
  expires_at: string;
  agent_id: string;
}

export type ChatEvent =
  | {
      kind: "start";
      conversation_id: string;
      user_message_id: string;
      /**
       * Per-conversation bearer present ONLY on the first turn of a
       * freshly created conversation. The widget stashes it to
       * localStorage so a future poll can prove "I am the visitor for
       * this conversation". CLAUDE.md sectie 3 (Hallon).
       */
      visitor_session_token: string | null;
    }
  | { kind: "delta"; text: string }
  | { kind: "tool_call"; tool_name: string }
  | { kind: "tool_result"; tool_name: string; ok: boolean }
  | { kind: "usage"; input_tokens: number; output_tokens: number }
  | { kind: "done"; auto_flagged_reason?: string }
  | { kind: "error"; message: string };

/**
 * One message returned by `GET /widget/v1/conversation/{id}/messages`.
 *
 * `role` is the string value of the backend `MessageRole` enum, so
 * the widget can branch on `"operator_email"` to render a "Bericht
 * van het team" label and on `"assistant"` for normal AI bubbles.
 */
export interface PolledMessage {
  id: string;
  role: string;
  content: string;
  created_at: string;
  source_url: string | null;
}

export interface PolledMessages {
  messages: PolledMessage[];
}

/**
 * Error wrapper for the polling fetch so the widget can branch on
 * "token mismatch" (kind `forbidden`) vs transport failure (`network`).
 * The widget responds to `forbidden` by clearing localStorage and
 * starting a fresh conversation; transport errors are swallowed (the
 * next poll retries on its own 30-second cadence).
 */
export type PollErrorKind = "forbidden" | "network" | "generic";
export class PollError extends Error {
  readonly kind: PollErrorKind;
  constructor(kind: PollErrorKind, message: string) {
    super(message);
    this.kind = kind;
    this.name = "PollError";
  }
}

function authHeader(boot: WidgetBootConfig): string {
  return `Widget ${boot.tenant}:${boot.key}`;
}

export async function fetchConfig(boot: WidgetBootConfig): Promise<WidgetTenantConfig> {
  // Append the visitor locale so the backend can resolve the opening
  // message in the visitor's language rather than the tenant's default
  // locale. A Dutch tenant serving an English visitor should see the
  // English boilerplate; the operator-authored value still wins when
  // set (see `services/widget_opening_message.py`).
  const params = boot.locale ? `?locale=${encodeURIComponent(boot.locale)}` : "";
  const url = `${boot.apiBaseUrl}/widget/v1/config${params}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: authHeader(boot),
      Accept: "application/json",
    },
  });
  if (!res.ok) {
    throw new Error(`Widget config failed: HTTP ${res.status}`);
  }
  return (await res.json()) as WidgetTenantConfig;
}

/**
 * Request a short-lived signed websocket URL for the voice modal.
 *
 * The slug + key live in the body (mirroring the chat
 * `Authorization` shape). A 503 means voice is not configured for
 * this tenant; the caller paraphrases that into a generic
 * connection-error microcopy and the widget hides the mic button
 * for the rest of the session.
 */
export async function fetchVoiceSession(boot: WidgetBootConfig): Promise<VoiceSession> {
  const url = `${boot.apiBaseUrl}/v1/voice/sessions`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      tenant: boot.tenant,
      key: boot.key,
      locale: boot.locale,
    }),
  });
  if (!res.ok) {
    throw new Error(`Voice session failed: HTTP ${res.status}`);
  }
  return (await res.json()) as VoiceSession;
}

/**
 * Content blocks for a chat turn. Plain text turns send `content` as
 * a string for backwards compatibility with the existing endpoint.
 * Mixed turns (text + attachment) send an array of typed blocks. PR
 * #22 / Slice 2: the widget no longer base64-encodes image bytes
 * inline; instead it uploads bytes first via `/widget/v1/uploads` and
 * references the resulting `attachment_id`.
 */
export interface TextBlock {
  type: "text";
  text: string;
}
export interface AttachmentBlock {
  type: "attachment";
  attachment_id: string;
}
export type ContentBlock = TextBlock | AttachmentBlock;

export interface ChatTurnRequest {
  role: "user" | "assistant";
  content: string | ContentBlock[];
}

/**
 * Response from `POST /widget/v1/uploads`. Mirrors the backend
 * Pydantic schema; the widget only reads `attachment_id` for the
 * subsequent chat turn but the other fields are useful for tests and
 * future debug surfaces.
 */
export interface UploadResponse {
  attachment_id: string;
  storage_path: string;
  mime_type: string;
  byte_size: number;
}

/**
 * Errors `uploadAttachment` can throw. The caller maps `kind` to the
 * locale-aware microcopy in the widget thread.
 */
export type UploadErrorKind = "too_large" | "unsupported_type" | "transport" | "generic";
export class UploadError extends Error {
  readonly kind: UploadErrorKind;
  constructor(kind: UploadErrorKind, message: string) {
    super(message);
    this.kind = kind;
    this.name = "UploadError";
  }
}

/**
 * Upload one image to `/widget/v1/uploads` as multipart form data.
 *
 * Auth header mirrors the chat endpoint (slug + key). The browser
 * sets the multipart boundary on `Content-Type` automatically when we
 * pass a `FormData` body, so we deliberately do NOT set that header.
 *
 * Status mapping matches the backend contract:
 *   - 201 -> `UploadResponse`
 *   - 413 -> `UploadError("too_large", ...)`
 *   - 415 -> `UploadError("unsupported_type", ...)`
 *   - 502 -> `UploadError("transport", ...)`
 *   - anything else -> `UploadError("generic", ...)`
 *
 * Network failures (offline, CORS) surface as `transport` errors so
 * the operator-facing string can stay calm.
 */
export async function uploadAttachment(
  boot: WidgetBootConfig,
  file: File,
  conversationId: string | null,
): Promise<UploadResponse> {
  const url = `${boot.apiBaseUrl}/widget/v1/uploads`;
  const form = new FormData();
  form.append("file", file);
  if (conversationId) form.append("conversation_id", conversationId);

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: authHeader(boot),
        Accept: "application/json",
      },
      body: form,
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : "network";
    throw new UploadError("transport", msg);
  }

  if (res.status === 201) {
    return (await res.json()) as UploadResponse;
  }
  if (res.status === 413) throw new UploadError("too_large", "file too large");
  if (res.status === 415) throw new UploadError("unsupported_type", "mime not allowed");
  if (res.status === 502) throw new UploadError("transport", "storage transport error");
  throw new UploadError("generic", `Upload failed: HTTP ${res.status}`);
}

/**
 * Pure projection from in-thread chat turns to the wire shape sent to
 * `/widget/v1/chat`. Lifted out of the React component so it is unit
 * testable in isolation.
 *
 * Rules:
 *  - The trailing empty assistant placeholder (the streaming slot the
 *    UI just inserted) is dropped; backend computes its own.
 *  - A turn with text and no attachment ships `content` as a string,
 *    matching the backwards-compatible wire shape.
 *  - A turn with at least one attachment_id ships `content` as an
 *    array of typed blocks: optional text first, then one
 *    `attachment` block per id. Attachments without an id (still
 *    uploading or failed) are silently skipped because they are not
 *    durable on the backend yet.
 *  - Turns with neither text nor a ready attachment are dropped.
 */
export function buildRequestMessages(
  turns: ReadonlyArray<{
    role: "user" | "assistant";
    text: string;
    attachments?: ReadonlyArray<{ attachment_id?: string }>;
  }>,
): ChatTurnRequest[] {
  return turns
    .filter((turn, i) => {
      if (i === turns.length - 1 && turn.role === "assistant") return false;
      if (turn.text.trim().length > 0) return true;
      if ((turn.attachments ?? []).some((a) => a.attachment_id)) return true;
      return false;
    })
    .map((turn): ChatTurnRequest => {
      const ids = (turn.attachments ?? [])
        .map((a) => a.attachment_id)
        .filter((id): id is string => typeof id === "string");
      if (ids.length === 0) {
        return { role: turn.role, content: turn.text };
      }
      const blocks: ContentBlock[] = [];
      if (turn.text.trim().length > 0) {
        blocks.push({ type: "text", text: turn.text });
      }
      for (const id of ids) blocks.push({ type: "attachment", attachment_id: id });
      return { role: turn.role, content: blocks };
    });
}

/**
 * Outcome of submitting the visitor's email after a no-source
 * escalation. The widget maps each variant onto a different UI state
 * (success line, silent success, inline error, generic warm retry).
 *
 *   - "saved": the row was null and is now stamped. Show the
 *     thank-you confirmation line.
 *   - "already_set": the row was already non-null (the visitor
 *     already shared an email earlier in this conversation, or the
 *     POST raced with a previous one). Hide the form, show the
 *     thank-you line: from the visitor's perspective this looks the
 *     same as a fresh capture.
 *   - "invalid": the backend rejected the address (malformed,
 *     too long). The widget shows an inline error under the field.
 *   - "transport": network failure or any other error. The widget
 *     shows a calm retry hint.
 */
export type SubmitCustomerEmailResult =
  | { kind: "saved" }
  | { kind: "already_set" }
  | { kind: "invalid" }
  | { kind: "transport" };

/**
 * POST the visitor's email to
 * `/widget/v1/conversation/{id}/customer-email` so the operator's
 * "Verstuur via email" button in the dashboard lights up.
 *
 * Status mapping mirrors the backend contract:
 *   - 204 -> `saved`
 *   - 409 -> `already_set` (we never overwrite; treat as success in
 *     the UI so a double-submit lands gracefully)
 *   - 400 / 404 / 422 -> `invalid`
 *   - anything else (network, 5xx) -> `transport`
 */
export async function submitCustomerEmail(
  boot: WidgetBootConfig,
  conversationId: string,
  email: string,
): Promise<SubmitCustomerEmailResult> {
  const url = `${boot.apiBaseUrl}/widget/v1/conversation/${encodeURIComponent(
    conversationId,
  )}/customer-email`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: authHeader(boot),
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });
  } catch {
    return { kind: "transport" };
  }
  if (res.status === 204) return { kind: "saved" };
  if (res.status === 409) return { kind: "already_set" };
  if (res.status === 400 || res.status === 404 || res.status === 422) {
    return { kind: "invalid" };
  }
  return { kind: "transport" };
}

export async function* streamChat(
  boot: WidgetBootConfig,
  conversationId: string | null,
  messages: ChatTurnRequest[],
): AsyncGenerator<ChatEvent, void, void> {
  const url = `${boot.apiBaseUrl}/widget/v1/chat`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: authHeader(boot),
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      conversation_id: conversationId,
      messages,
    }),
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Widget chat failed: HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const normalise = (s: string): string => s.replace(/\r\n/g, "\n");

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += normalise(decoder.decode(value, { stream: true }));

    // SSE frames are separated by a blank line ("\n\n"). Process every
    // complete frame in the buffer, leave the partial one for the next
    // chunk.
    let frameEnd = buffer.indexOf("\n\n");
    while (frameEnd !== -1) {
      const frame = buffer.slice(0, frameEnd);
      buffer = buffer.slice(frameEnd + 2);
      frameEnd = buffer.indexOf("\n\n");

      const parsed = parseFrame(frame);
      if (parsed !== null) yield parsed;
    }
  }
}

function parseFrame(frame: string): ChatEvent | null {
  let event = "message";
  let dataLine = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLine += line.slice(5).trim();
    }
  }
  if (!dataLine) return null;

  let payload: Record<string, unknown> = {};
  try {
    payload = JSON.parse(dataLine) as Record<string, unknown>;
  } catch {
    return null;
  }

  switch (event) {
    case "start":
      if (
        typeof payload.conversation_id === "string" &&
        typeof payload.user_message_id === "string"
      ) {
        return {
          kind: "start",
          conversation_id: payload.conversation_id,
          user_message_id: payload.user_message_id,
          visitor_session_token:
            typeof payload.visitor_session_token === "string"
              ? payload.visitor_session_token
              : null,
        };
      }
      return null;
    case "delta":
      if (typeof payload.text === "string") {
        return { kind: "delta", text: payload.text };
      }
      return null;
    case "tool_call":
      if (typeof payload.tool_name === "string") {
        return { kind: "tool_call", tool_name: payload.tool_name };
      }
      return null;
    case "tool_result":
      if (typeof payload.tool_name === "string") {
        return {
          kind: "tool_result",
          tool_name: payload.tool_name,
          ok: payload.ok !== false,
        };
      }
      return null;
    case "usage":
      return {
        kind: "usage",
        input_tokens: typeof payload.input_tokens === "number" ? payload.input_tokens : 0,
        output_tokens: typeof payload.output_tokens === "number" ? payload.output_tokens : 0,
      };
    case "error":
      if (typeof payload.message === "string") {
        return { kind: "error", message: payload.message };
      }
      return null;
    case "done": {
      // The no-source pre-gate stamps `auto_flagged_reason` on the
      // closing event so the widget can render the email-capture form
      // for anonymous visitors. The good-path `done` ships an empty
      // payload, so the field stays undefined for the regular case.
      const reason = payload.auto_flagged_reason;
      if (typeof reason === "string" && reason.length > 0) {
        return { kind: "done", auto_flagged_reason: reason };
      }
      return { kind: "done" };
    }
    default:
      return null;
  }
}

/**
 * Poll for new messages on one conversation.
 *
 * Backed by `GET /widget/v1/conversation/{id}/messages?after=...`. The
 * widget calls this every 30 seconds WHILE the panel is open AND
 * `document.visibilityState === "visible"`, so a backgrounded tab
 * does not hammer the API.
 *
 * Auth carries two headers: the existing widget `Authorization` (tenant
 * key + slug) AND a per-conversation `X-Visitor-Session` bearer. The
 * backend bounces a missing or mismatched bearer with 403, which the
 * widget translates into "clear localStorage + start fresh". Network
 * failures (offline, CORS) become `PollError("network", ...)` so the
 * caller can keep the catch path calm.
 *
 * `after` MUST be an ISO-8601 timestamp; the helper takes a Date for
 * call-site ergonomics and formats it consistently.
 */
export async function fetchNewMessages(
  boot: WidgetBootConfig,
  conversationId: string,
  visitorSessionToken: string,
  after: Date,
): Promise<PolledMessage[]> {
  const params = new URLSearchParams({ after: after.toISOString() });
  const url = `${boot.apiBaseUrl}/widget/v1/conversation/${encodeURIComponent(
    conversationId,
  )}/messages?${params.toString()}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: authHeader(boot),
        Accept: "application/json",
        "X-Visitor-Session": visitorSessionToken,
      },
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : "network";
    throw new PollError("network", msg);
  }

  if (res.status === 403) {
    throw new PollError("forbidden", "visitor session rejected");
  }
  if (!res.ok) {
    throw new PollError("generic", `Poll failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as PolledMessages;
  return body.messages ?? [];
}
